(() => {
  const storageKey = 'libero_discount_codes';
  const maxCodes = 5;
  const root = window.Shopify?.routes?.root || '/';

  const getCodes = () => {
    try { return JSON.parse(sessionStorage.getItem(storageKey) || '[]'); } catch (_) { return []; }
  };
  const saveCodes = (codes) => sessionStorage.setItem(storageKey, JSON.stringify(codes));
  const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency', currency: currency || window.Shopify?.currency?.active || 'USD'
  }).format((cents || 0) / 100);
  const text = (panel, key, fallback) => panel?.dataset[key] || fallback;

  const renderUnitPrice = (target, item, currency) => {
    if (!target) return;
    target.replaceChildren();
    if (item.original_price !== item.final_price) {
      target.className = 'cart-item__discounted-prices';
      const oldPrice = document.createElement('s');
      oldPrice.className = 'cart-item__old-price product-option';
      oldPrice.textContent = money(item.original_price, currency);
      const finalPrice = document.createElement('strong');
      finalPrice.className = 'cart-item__final-price product-option';
      finalPrice.textContent = money(item.final_price, currency);
      target.append(oldPrice, finalPrice);
    } else {
      target.className = 'product-option';
      target.textContent = money(item.original_price, currency);
    }
    target.setAttribute('data-cart-unit-price', '');
  };

  const renderLineTotal = (target, item, currency) => {
    if (!target) return;
    target.replaceChildren();
    if (item.original_line_price !== item.final_line_price) {
      const prices = document.createElement('div');
      prices.className = 'cart-item__discounted-prices';
      const oldPrice = document.createElement('s');
      oldPrice.className = 'cart-item__old-price price price--end';
      oldPrice.textContent = money(item.original_line_price, currency);
      const finalPrice = document.createElement('span');
      finalPrice.className = 'price price--end';
      finalPrice.textContent = money(item.final_line_price, currency);
      prices.append(oldPrice, finalPrice);
      target.append(prices);
    } else {
      const price = document.createElement('span');
      price.className = 'price price--end';
      price.textContent = money(item.original_line_price, currency);
      target.append(price);
    }
  };

  const renderLineDiscounts = (target, item) => {
    if (!target) return;
    const panel = document.querySelector('cart-discount-panel');
    target.replaceChildren(...(item.line_level_discount_allocations || []).map((allocation) => {
      const label = document.createElement('li');
      label.className = 'discounts__discount';
      label.textContent = allocation.discount_application?.title || text(panel, 'i18nProductDiscount', 'Product discount');
      return label;
    }));
  };

  const updateLineUnitPrices = (cart) => {
    (cart.items || []).forEach((item, index) => {
      const line = index + 1;
      [`CartItem-${line}`, `CartDrawer-Item-${line}`].forEach((id) => {
        const row = document.getElementById(id);
        renderUnitPrice(row?.querySelector('[data-cart-unit-price]'), item, cart.currency);
        row?.querySelectorAll('[data-cart-line-total]').forEach((target) => renderLineTotal(target, item, cart.currency));
        renderLineDiscounts(row?.querySelector('[data-cart-line-discounts]'), item);
      });
    });
  };

  class CartDiscountPanel extends HTMLElement {
    connectedCallback() {
      this.querySelector('form')?.addEventListener('submit', (event) => this.apply(event));
      this.querySelector('[data-discount-suggestion]')?.addEventListener('click', (event) => {
        const input = this.querySelector('input[name="discount"]');
        const form = this.querySelector('form');
        if (!input || !form) return;
        input.value = event.currentTarget.dataset.discountSuggestion || '';
        form.requestSubmit();
      });
      this.querySelector('.cart-discount__review')?.addEventListener('click', () => this.reviewItems());
      const currentCodes = (this.dataset.currentCodes || '').split(',').map((code) => code.trim()).filter(Boolean);
      saveCodes([...new Set(currentCodes)]);
      this.renderCodes();
      this.updateDisclosure(Number(this.dataset.totalDiscount || 0));
    }

    updateDisclosure(totalDiscount, currency = this.dataset.currency) {
      const applied = totalDiscount > 0;
      const details = this.querySelector('.cart-discount__details');
      const label = this.querySelector('[data-discount-summary-label]');
      const action = this.querySelector('[data-discount-summary-action]');
      const review = this.querySelector('.cart-discount__review');
      const suggestion = this.querySelector('.cart-discount__suggestion');
      details?.classList.toggle('is-applied', applied);
      review?.classList.toggle('is-hidden', !applied);
      suggestion?.classList.toggle('is-hidden', applied);
      if (label) label.textContent = applied
        ? text(this, 'i18nDiscountApplied', 'Discount applied · You saved __AMOUNT__').replace('__AMOUNT__', money(totalDiscount, currency))
        : text(this, 'i18nHaveCode', 'Have a discount code?');
      if (action) action.textContent = applied ? text(this, 'i18nChange', 'Change') : text(this, 'i18nAddCode', 'Add code');
      if (applied && details) details.open = false;
    }

    reviewItems() {
      const drawer = this.closest('cart-drawer');
      const target = drawer?.querySelector('#CartDrawer-CartItems') || document.getElementById('main-cart-items');
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      if (drawer) {
        drawer.querySelector('cart-drawer-items')?.scrollTo({ top: 0, behavior });
        drawer.querySelector('.drawer__inner')?.scrollTo({ top: 0, behavior });
      } else {
        target?.scrollIntoView({ behavior, block: 'start' });
      }
      target?.setAttribute('tabindex', '-1');
      window.setTimeout(() => target?.focus({ preventScroll: true }), 450);
    }

    setMessage(text, type = '') {
      const message = this.querySelector('.cart-discount__message');
      if (!message) return;
      message.textContent = text;
      message.className = `cart-discount__message${type ? ` is-${type}` : ''}`;
    }

    async apply(event) {
      event.preventDefault();
      const input = this.querySelector('input[name="discount"]');
      const code = input.value.trim().toUpperCase();
      if (!code) return this.setMessage(text(this, 'i18nEnterCode', 'Enter a discount code.'), 'error');
      const codes = [...new Set([...getCodes(), code])];
      if (codes.length > maxCodes) return this.setMessage(text(this, 'i18nTooManyCodes', 'You can use up to 5 discount codes. Remove one before adding another.'), 'error');
      await CartDiscountPanel.update(codes, code);
      input.value = '';
    }

    renderCodes(statuses = new Map()) {
      const target = this.querySelector('.cart-discount__codes');
      if (!target) return;
      target.replaceChildren(...getCodes().map((code) => {
        const chip = document.createElement('span');
        chip.className = 'cart-discount__code';
        chip.append(document.createTextNode(code));
        if (statuses.has(code)) {
          const status = document.createElement('span');
          status.className = `cart-discount__code-status ${statuses.get(code) ? 'is-applied' : 'is-not-applied'}`;
          status.textContent = statuses.get(code) ? text(this, 'i18nApplied', 'Applied') : text(this, 'i18nNotApplied', 'Not applied');
          chip.append(status);
        }
        const remove = document.createElement('button');
        remove.type = 'button'; remove.className = 'cart-discount__remove';
        remove.setAttribute('aria-label', text(this, 'i18nRemoveCode', 'Remove discount code __CODE__').replace('__CODE__', code));
        const closeIcon = document.createElement('img');
        closeIcon.src = this.dataset.closeIcon;
        closeIcon.alt = '';
        closeIcon.width = 18;
        closeIcon.height = 17;
        remove.append(closeIcon);
        remove.addEventListener('click', () => CartDiscountPanel.update(getCodes().filter((item) => item !== code)));
        chip.append(remove); return chip;
      }));
    }

    static async update(codes, newestCode = '') {
      const panels = [...document.querySelectorAll('cart-discount-panel')];
      panels.forEach((panel) => { panel.classList.add('is-loading'); panel.setMessage(text(panel, 'i18nApplying', 'Applying discount…')); });
      document.querySelectorAll('[name="checkout"]').forEach((button) => button.disabled = true);
      try {
        const response = await fetch(`${root}cart/update.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: codes.join(',') })
        });
        if (!response.ok) throw new Error('Discount update failed');
        const cart = await response.json();
        const statuses = new Map((cart.discount_codes || []).map((entry) => [entry.code.toUpperCase(), entry.applicable]));
        saveCodes(codes.slice(0, maxCodes));
        updateLineUnitPrices(cart);
        const applications = new Map();
        (cart.cart_level_discount_applications || []).forEach((app) => applications.set(app.title, app.total_allocated_amount || 0));
        (cart.items || []).forEach((item) => (item.line_level_discount_allocations || []).forEach((entry) => {
          const title = entry.discount_application?.title || text(panels[0], 'i18nProductDiscount', 'Product discount');
          applications.set(title, (applications.get(title) || 0) + (entry.amount || 0));
        }));
        panels.forEach((panel) => {
          panel.classList.remove('is-loading'); panel.renderCodes(statuses);
          panel.querySelector('[data-discount-original]').textContent = money(cart.original_total_price, cart.currency);
          panel.querySelector('[data-discount-saving]').textContent = `-${money(cart.total_discount, cart.currency)}`;
          panel.querySelector('[data-discount-total]').textContent = money(cart.total_price, cart.currency);
          panel.dataset.totalDiscount = cart.total_discount;
          panel.updateDisclosure(cart.total_discount, cart.currency);
          panel.querySelector('.cart-discount__row--saving').classList.toggle('is-hidden', !cart.total_discount);
          const apps = panel.querySelector('.cart-discount__applications');
          apps.replaceChildren(...[...applications.keys()].map((title) => { const tag = document.createElement('span'); tag.textContent = title; return tag; }));
          apps.classList.toggle('is-hidden', applications.size === 0);
          const newestApplied = newestCode ? statuses.get(newestCode.toUpperCase()) === true : false;
          panel.setMessage(
            newestCode
              ? (newestApplied
                  ? text(panel, 'i18nUpdateSuccess', 'Discount updated. The best eligible discount is applied to each item.')
                  : text(panel, 'i18nCodeNotApplied', 'This code does not apply, or a better discount is already applied to this item.'))
              : text(panel, 'i18nRemoved', 'Discount code removed.'),
            newestCode ? (newestApplied ? 'success' : 'error') : 'success'
          );
        });
        document.dispatchEvent(new CustomEvent('libero:discount-updated', { detail: cart }));
      } catch (error) {
        panels.forEach((panel) => { panel.classList.remove('is-loading'); panel.setMessage(text(panel, 'i18nUpdateFailed', 'We could not apply that code. Please try again.'), 'error'); });
      } finally {
        document.querySelectorAll('[name="checkout"]').forEach((button) => button.disabled = false);
      }
    }
  }

  if (!customElements.get('cart-discount-panel')) customElements.define('cart-discount-panel', CartDiscountPanel);
})();
